import React, {
  createContext,
  useContext,
} from 'react';
import {
  VirtualTableColumnDef,
} from 'core/components/VirtualTable/types';
import {
  MultimediaTypeWithCategory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import FilePicker, {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  UploadIcon,
} from 'core/icons';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import ToolTip from 'core/components/Tooltip';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import Tag from 'core/components/Tag';
import Button from 'pages/Dashboard/components/Button';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import {
  useMixpanelTrack,
} from 'core/hooks';

export type FileUploadDialogContextValue = {
  isSaving: boolean;
  isLimitReached: boolean;
};

export const FileUploadDialogContext = createContext<FileUploadDialogContextValue>({
  isSaving: false,
  isLimitReached: false,
});

type UploadActionsCellProps = {
  original: MultimediaTypeWithCategory;
};

function UploadActionsCell({ original }: UploadActionsCellProps) {
  const { isSaving, isLimitReached } = useContext(FileUploadDialogContext);
  const allowedFileTypes = useGetAllowedFileTypes();
  const mixpanelTrack = useMixpanelTrack();

  return (
    <div className="truncate flex justify-end flex-1">
      <div className="flex items-center gap-2">
        <ToolTip
          title={isLimitReached ? 'Maximum 10 files can be uploaded at a time.' : 'Drag and drop your media file to upload or click to browse'}
          hide={isSaving}
        >
          <span className={(isSaving || isLimitReached) ? 'opacity-50' : ''}>
            <FilePicker
              onFileUpload={async (files) => {
                if (!isSaving && !isLimitReached) {
                  mediaEventChannel.emit('onMediaUploadedToCategory', {
                    files: [...(original?.files ?? []), ...files],
                    original,
                  });
                }
              }}
              variant="outlined"
              color="secondary"
              className="!px-2 !h-6 !text-gray-600 !rounded-sm !normal-case flex items-center gap-1 whitespace-nowrap"
              allowedFileTypes={allowedFileTypes}
              multiple
              disabled={isSaving || isLimitReached}
            >
              <UploadIcon className="!w-3 !h-3" />
            </FilePicker>
          </span>
        </ToolTip>
        <span className={isSaving ? 'opacity-50' : ''}>
          <ToolTip title={(isLimitReached ? 'Maximum 10 files can be uploaded at a time.' : 'Click to capture photos from webcam')}>
            <span>
              <Button
                variant="outlined"
                color="secondary"
                disabled={isSaving || isLimitReached}
                className="!px-2 !h-6 !text-gray-600 !rounded-sm !normal-case flex items-center gap-1 whitespace-nowrap"
                onClick={() => {
                  mixpanelTrack('onStartWebcamScanFromMultimediaDialog');
                  mediaEventChannel.emit('onStartWebcamScan', {
                    captureType: 'camera-capture',
                    limit: 10, // todo fix limit count
                    onCapturedFilesConfirmed: (files: FileLocalUrl[]) => {
                      mediaEventChannel.emit('onMediaUploadedToCategory', {
                        files: [...(original?.files ?? []), ...files],
                        original,
                      });
                    },
                  });
                }}
              >
                <AddAPhotoOutlinedIcon className="!w-4 !h-4" />
              </Button>
            </span>
          </ToolTip>
        </span>
      </div>
    </div>
  );
}

export const columns: VirtualTableColumnDef<MultimediaTypeWithCategory>[] = [
  {
    id: 'label',
    header: 'Type',
    meta: { autoSized: true },
    cell: ({ row: { original } }) => {
      const { files = [] } = original;
      const fileExisits = files.length > 0;
      return (
        <div className="flex flex-col gap-1">
          <div className="flex gap-1 items-center">
            <div className="truncate font-semibold">
              {original.label}
            </div>
            {fileExisits && (
              <Tag
                tagName={`${files.length} files`}
                defaultColor="bg-gray-200"
              />
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'action',
    header: '',
    size: 150,
    cell: ({ row: { original } }) => <UploadActionsCell original={original} />,
  },
];
